/**
 * src/components/BottomNav.jsx
 *
 * 5-tab dark bottom navigation bar for mobile.
 * Design: Dark #111827 background, rounded-top corners, filled/outline icon toggle,
 *         active tab = white filled icon + white bold label + rose pill indicator.
 *
 * Hidden on md: (768px+) — desktop uses the existing Sidebar.
 * Tabs: Home / Bookings / Requests / Status / Profile
 */

import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// ── Icons (outline = inactive, filled = active) ───────────────────────────────

const HomeIcon = ({ filled }) =>
  filled ? (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
      <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
      <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
    </svg>
  ) : (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      className="w-6 h-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
      />
    </svg>
  );

const BookingsIcon = ({ filled }) =>
  filled ? (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
      <path
        fillRule="evenodd"
        d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3A.75.75 0 0118 3v1.5h.75a3 3 0 013 3v11.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V7.5a3 3 0 013-3H6V3a.75.75 0 01.75-.75zm13.5 9a1.5 1.5 0 00-1.5-1.5H5.25a1.5 1.5 0 00-1.5 1.5v7.5a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5v-7.5z"
        clipRule="evenodd"
      />
    </svg>
  ) : (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      className="w-6 h-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 9v7.5"
      />
    </svg>
  );

const RequestsIcon = ({ filled }) =>
  filled ? (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
      <path
        fillRule="evenodd"
        d="M7.5 5.25a3 3 0 013-3h3a3 3 0 013 3v.205c.933.085 1.857.197 2.774.334 1.454.218 2.476 1.483 2.476 2.917v3.033c0 1.211-.734 2.352-1.936 2.752A24.726 24.726 0 0112 15.75c-2.73 0-5.357-.442-7.814-1.259-1.202-.4-1.936-1.541-1.936-2.752V8.706c0-1.434 1.022-2.7 2.476-2.917A48.814 48.814 0 017.5 5.455V5.25zm7.5 0v.09a49.488 49.488 0 00-6 0v-.09a1.5 1.5 0 011.5-1.5h3a1.5 1.5 0 011.5 1.5zm-3 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
        clipRule="evenodd"
      />
      <path d="M3 18.4v-2.796a4.3 4.3 0 00.713.31A26.226 26.226 0 0012 17.25c2.93 0 5.748-.478 8.287-1.336.252-.085.496-.186.713-.311V18.4c0 1.452-1.047 2.728-2.523 2.923-2.12.282-4.282.427-6.477.427a49.19 49.19 0 01-6.477-.427C4.047 21.128 3 19.852 3 18.4z" />
    </svg>
  ) : (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      className="w-6 h-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z"
      />
    </svg>
  );

const StatusIcon = ({ filled }) =>
  filled ? (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
      <path
        fillRule="evenodd"
        d="M7.502 6h7.128A3.375 3.375 0 0118 9.375v9.375a3 3 0 003-3V6.108c0-1.505-1.125-2.811-2.664-2.94a48.972 48.972 0 00-8.583-.19 2.897 2.897 0 00-2.25 2.272v.75zm7.123 2.25H6.375a2.625 2.625 0 00-2.625 2.625v9.375a2.625 2.625 0 002.625 2.625h8.25a2.625 2.625 0 002.625-2.625V10.875A2.625 2.625 0 0014.625 8.25zm-2.344 4.5a.75.75 0 01.75.75v1.5h1.5a.75.75 0 010 1.5h-1.5v1.5a.75.75 0 01-1.5 0V16.5H9.53a.75.75 0 010-1.5h1.5v-1.5a.75.75 0 01.75-.75z"
        clipRule="evenodd"
      />
    </svg>
  ) : (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      className="w-6 h-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 3h1a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C8.095 4.78 7.25 5.694 7.25 6.83v1.96M4.875 20.25h9.75a2.625 2.625 0 002.625-2.625V10.875a2.625 2.625 0 00-2.625-2.625H4.875A2.625 2.625 0 002.25 10.875v6.75A2.625 2.625 0 004.875 20.25z"
      />
    </svg>
  );

const ProfileIcon = ({ filled }) =>
  filled ? (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
      <path
        fillRule="evenodd"
        d="M18.685 19.097A9.723 9.723 0 0021.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 003.065 7.097A9.716 9.716 0 0012 21.75a9.716 9.716 0 006.685-2.653zm-12.54-1.285A7.486 7.486 0 0112 15a7.486 7.486 0 015.855 2.812A8.224 8.224 0 0112 20.25a8.224 8.224 0 01-5.855-2.438zM15.75 9a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
        clipRule="evenodd"
      />
    </svg>
  ) : (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      className="w-6 h-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
      />
    </svg>
  );

// ── Tab config ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: "home", labelKey: "bottomNav.home", label: "Home", path: "/caregiver/dashboard", Icon: HomeIcon },
  {
    id: "bookings",
    labelKey: "bottomNav.bookings",
    label: "Bookings",
    path: "/caregiver/bookings",
    Icon: BookingsIcon,
  },
  {
    id: "requests",
    labelKey: "bottomNav.requests",
    label: "Requests",
    path: "/caregiver/request/apply",
    Icon: RequestsIcon,
  },
  {
    id: "status",
    labelKey: "bottomNav.status",
    label: "Status",
    path: "/caregiver/application-status",
    Icon: StatusIcon,
  },
  {
    id: "profile",
    labelKey: "bottomNav.profile",
    label: "Profile",
    path: "/caregiver/profile",
    Icon: ProfileIcon,
  },
];

// ── Single Tab ─────────────────────────────────────────────────────────────────

function NavTab({ tab, isActive, onClick }) {
  const { Icon } = tab;
  const { t } = useTranslation();

  return (
    <button
      onClick={() => onClick(tab)}
      className="flex-1 flex flex-col items-center justify-center gap-1 py-2 relative min-h-[56px] focus:outline-none active:scale-[0.88] transition-transform duration-100"
      aria-label={t(tab.labelKey)}
      aria-current={isActive ? "page" : undefined}
    >
      {/* Active indicator pill */}
      {isActive && (
        <span
          className="absolute top-0 w-8 h-[3px] rounded-b-full bg-primary"
          style={{ left: "50%", marginLeft: "-16px" }}
        />
      )}

      {/* Icon */}
      <span className={isActive ? "text-white" : "text-gray-400"}>
        <Icon filled={isActive} />
      </span>

      {/* Label */}
      <span
        className={`text-[10px] leading-none tracking-wide ${
          isActive ? "text-white font-bold" : "text-gray-400 font-normal"
        }`}
      >
        {t(tab.labelKey)}
      </span>
    </button>
  );
}

// ── Nav Background (solid dark fill behind rounded corners) ───────────────────

function NavBackground() {
  return (
    <div
      className="absolute inset-x-0 bottom-0 h-full"
      style={{ backgroundColor: "#111827" }}
    />
  );
}

function NavBarInner({ children }) {
  return (
    <div
      className="relative flex items-stretch shadow-[0_-2px_8px_rgba(0,0,0,0.12)]"
      style={{
        backgroundColor: "#111827",
        borderRadius: "18px 18px 0 0",
      }}
    >
      {children}
    </div>
  );
}

// ── Main BottomNav ─────────────────────────────────────────────────────────────

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();

  const getActiveTab = () => {
    const path = location.pathname;
    if (path === "/caregiver/dashboard" || path === "/") return "home";
    if (path.startsWith("/caregiver/bookings")) return "bookings";
    if (path.startsWith("/caregiver/request")) return "requests";
    if (path.startsWith("/caregiver/application-status")) return "status";
    if (path.startsWith("/caregiver/profile")) return "profile";
    return "home";
  };

  const activeTab = getActiveTab();

  const handleTabClick = (tab) => {
    // Don't re-navigate to current route
    if (location.pathname === tab.path) return;

    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    navigate(tab.path);
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-[9980] md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      {/* Solid fill behind rounded corners so page content doesn't peek through */}
      <NavBackground />

      <NavBarInner>
        {TABS.map((tab) => (
          <NavTab
            key={tab.id}
            tab={tab}
            isActive={activeTab === tab.id}
            onClick={handleTabClick}
          />
        ))}
      </NavBarInner>
    </nav>
  );
}
